import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { UserPlus, Copy, Share2, CheckCircle2 } from "lucide-react";

/**
 * "Convidar Colegas" panel — gives each player a unique magic link they can share.
 * When a new joiner clicks the link, the OnboardingWizard pre-fills the recruiter step
 * with this player. After confirmation, the recruiter receives +20 GS_internal at the
 * new joiner's first reporting (handled by _auto_credit_growth_share).
 */
export default function InvitePanel() {
  const [link, setLink] = useState(null);
  const [usedCount, setUsedCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/invites/my-link").then((r) => {
      const origin = window.location.origin;
      setLink(`${origin}/?ref=${r.data.ref}`);
      setUsedCount(r.data.used_count || 0);
    }).catch(() => {});
  }, []);

  if (!link) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copiado — partilha já com um colega!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar. Seleciona o texto manualmente.");
    }
  };

  const shareNative = async () => {
    const text = `Junta-te a mim na KW Lead Growth League — 10 semanas a coopetir em equipa, com pontos por contactos, reuniões, angariações, vendas e marca pessoal:`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "KW Lead Growth League", text, url: link });
      } else {
        copy();
      }
    } catch { /* user cancelled — ignore */ }
  };

  return (
    <div className="kw-card p-6" data-testid="invite-panel">
      <div className="flex items-center gap-2 mb-3 border-b border-[#222] pb-3">
        <UserPlus className="w-5 h-5 text-[#E50000]" />
        <div className="flex-1">
          <div className="kw-overline">Mecanismo viral</div>
          <h2 className="font-display text-2xl font-black uppercase">Convidar Colegas</h2>
        </div>
        {usedCount > 0 && (
          <div className="text-right">
            <div className="font-display text-3xl font-black text-[#E50000]" data-testid="invite-used-count">{usedCount}</div>
            <div className="text-[10px] text-[#737373] uppercase tracking-widest">já aceitaram</div>
          </div>
        )}
      </div>
      <p className="text-sm text-[#A3A3A3] mb-4">
        Partilha o teu link único com colegas da KW Lead. Quando alguém entrar pelo teu link e confirmar o onboarding, ganhas <strong className="text-white">+20 pts</strong> automaticamente ao primeiro reporting dele(a).
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-0 border border-[#222] bg-[#0A0A0A] px-3 py-2 text-xs font-mono truncate" data-testid="invite-link-display">
          {link}
        </div>
        <button onClick={copy} className="kw-btn-secondary inline-flex items-center gap-2 !py-2 text-xs" data-testid="invite-copy-btn">
          {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E]" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copiado" : "Copiar"}
        </button>
        <button onClick={shareNative} className="kw-btn-primary inline-flex items-center gap-2 !py-2 text-xs" data-testid="invite-share-btn">
          <Share2 className="w-3.5 h-3.5" />
          Partilhar
        </button>
      </div>
      <div className="text-[10px] text-[#737373] uppercase tracking-widest mt-3">
        Quando o link for usado, recebes um email com o nome do(a) convidado(a).
      </div>
    </div>
  );
}
